import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { holdsConsoleRole, useSessionStore } from '@shared/auth/session';
import { readNextParam } from '@shared/auth/next-target';
import { Alert } from '@shared/ui/Alert';
import { MailIcon } from '@shared/ui/icons';

import { AuthCard, AuthHead, AuthShell } from '../_shared/AuthShell';
import {
  ActionRow,
  AuthButton,
  AuthFoot,
  Field,
  FormError,
  RequiredNote,
  useOnline,
} from '../_shared/components';
import { AUTH_ROUTES, OTP_USAGE } from '../_shared/constants';
import { authCopy } from '../_shared/copy';
import { toFieldFailure, toFormFailure, type FormFailure } from '../_shared/failure';
import { useAuthConfiguration, useCreateSession, useRequestOtp } from '../_shared/queries';
import type { AuthHandoff } from '../_shared/types';

import { copy } from './copy';
import { loginSchema, type LoginFields, type LoginValues } from './forms.schema';

/**
 * The operator sign-in screen — one identity screen against the same
 * `backend-auth` the subscriber host uses, drawn here because a cookie issued
 * on `admin.shahedni.com` is never sent to `my.shahedni.com`. What is duplicated
 * is the screen; what is not duplicated is anything behind it.
 *
 * **One form with two submit buttons**, not two forms and not a method
 * switcher. The accepted set follows `AUTH_METHODS`, read from
 * `GET /api/v1/authConfiguration` — a platform accepting one method shows no
 * choice, and the second is configured away rather than hidden in the client.
 * The two acts separate on which button was pressed: a password authenticates
 * in one step and goes straight to the console, a code does not and goes to the
 * verification screen.
 *
 * **The password field carries no required marker**, and the absence is the
 * point: an account without a password is a valid account, and it is the state
 * every account starts in. A red asterisk there would tell the majority of
 * accounts they are missing something.
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const online = useOnline();

  const next = readNextParam(searchParams.get('next'));

  /**
   * The guard sends a real session here when it holds no console role. That is
   * a refusal, not an error — the account authenticated correctly — so it
   * renders as the screen's standing notice rather than as a failure banner.
   */
  const [roleRefused, setRoleRefused] = useState(searchParams.get('reason') === 'role');
  const [failure, setFailure] = useState<FormFailure | null>(null);

  const configuration = useAuthConfiguration();
  const requestOtp = useRequestOtp();
  const createSession = useCreateSession();

  /**
   * `AUTH_METHODS` decides whether the second method exists on the screen at
   * all. While the read is in flight the block is **absent** rather than
   * skeletoned: a form control that appears a beat after the form did is worse
   * than one that was never there, and the primary act needs neither.
   */
  const methods = configuration.data?.auth_methods ?? [];
  const passwordEnabled = methods.includes('password');

  const form = useForm<LoginFields, unknown, LoginValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: { phone: '', password: '', method: 'otp' },
  });

  /**
   * The recovery link hands the number on in location state, and the value has
   * to be **subscribed** rather than read. `getValues` is evaluated while the
   * element is built, and this form is uncontrolled — no keystroke re-renders
   * the page on its own, so the link would carry the empty default it was built
   * with and the recovery screen, which bounces a stateless visit back here,
   * would never open.
   */
  const phone = useWatch({ control: form.control, name: 'phone' });

  const pending = requestOtp.isPending || createSession.isPending;

  useEffect(() => {
    document.title = copy.documentTitle;
  }, []);

  async function onSubmit(values: LoginValues) {
    setFailure(null);

    try {
      if (values.method === 'password') {
        await createSession.mutateAsync({ phone: values.phone, password: values.password });

        // Authentication succeeded and the console still may not open. The
        // session is cleared rather than left live behind a refusal: a token in
        // memory for an account that cannot use it is a session nobody asked
        // for.
        if (!holdsConsoleRole(useSessionStore.getState().role)) {
          useSessionStore.getState().clear();
          setRoleRefused(true);
          return;
        }

        navigate(next, { replace: true });
        return;
      }

      const { retry_after } = await requestOtp.mutateAsync({
        phone: values.phone,
        usage_type: OTP_USAGE.login,
      });

      // The number travels in location state, never in the URL: it is the
      // account's credential, and a query parameter would put it in history, in
      // the referrer, and in the edge's access log.
      // Converted once, here: `retry_after` is only true at the instant the
      // service answered, and this value has to survive a reload.
      const handoff: AuthHandoff = {
        phone: values.phone,
        barredUntil: Date.now() + retry_after * 1000,
        next,
      };
      navigate(AUTH_ROUTES.verify, { state: handoff });
    } catch (error) {
      const fieldFailure = toFieldFailure(error);
      if (fieldFailure) {
        form.setError(fieldFailure.field as keyof LoginFields, { message: fieldFailure.message });
      }
      setFailure(toFormFailure(error));
    }
  }

  return (
    <AuthShell>
      <AuthHead title={copy.head.title}>{copy.head.lede}</AuthHead>

      {roleRefused ? (
        <Alert kind="warning" title={authCopy.roleNotice.title} className="mb-5">
          {authCopy.roleNotice.body}
        </Alert>
      ) : null}

      {failure ? <FormError message={failure.message} tone={failure.tone} /> : null}

      <AuthCard>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <RequiredNote />

          {/*
            `dir="ltr"` on the control while its label, hint and error stay
            Arabic: the value is sent Latin and an identifier transcribed into
            another digit set is a wrong identifier. There is no placeholder —
            a placeholder is not a label and it leaves the moment typing starts,
            so the example lives in the hint where it survives.
          */}
          <Field
            {...form.register('phone')}
            id="a-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            dir="ltr"
            label={copy.phone.label}
            requiredMark
            hint={
              <>
                {copy.phone.hintPrefix}
                <bdi>{copy.phone.hintExample}</bdi>
                {copy.phone.hintSuffix}
              </>
            }
            {...(form.formState.errors.phone?.message ? { error: form.formState.errors.phone.message } : {})}
          />

          <ActionRow>
            <AuthButton
              type="submit"
              size="lg"
              disabled={pending}
              onClick={() => form.setValue('method', 'otp')}
            >
              <MailIcon />
              {online ? copy.actions.requestCode : copy.actions.offline}
            </AuthButton>
          </ActionRow>

          {/*
            Separated by a rule, not a heading: the second method is an
            alternative to the action above it rather than a second section of
            the page, and a heading would put it in the heading hierarchy.

            It is drawn open rather than folded behind a disclosure — a fold
            needs script to open, so an operator whose script failed would lose
            a method the configuration permits.
          */}
          {passwordEnabled ? (
            <div className="mt-5 border-t border-border pt-5">
              <Field
                {...form.register('password')}
                id="a-pass"
                type="password"
                revealable
                autoComplete="current-password"
                dir="ltr"
                label={copy.password.label}
                hint={copy.password.hint}
                {...(form.formState.errors.password?.message
                  ? { error: form.formState.errors.password.message }
                  : {})}
              />

              <ActionRow>
                <AuthButton
                  type="submit"
                  tone="secondary"
                  disabled={pending}
                  onClick={() => form.setValue('method', 'password')}
                >
                  {copy.actions.signInWithPassword}
                </AuthButton>
              </ActionRow>

              <AuthFoot>
                <Link to={AUTH_ROUTES.recover} state={{ phone }}>
                  {copy.foot.forgot}
                </Link>
              </AuthFoot>
            </div>
          ) : null}
        </form>
      </AuthCard>
    </AuthShell>
  );
}
